import React, { useState } from 'react';
import { motion } from 'framer-motion';
import LiquidGlassButton from './Button';

const ButtonDemo = () => {
  const [clickCount, setClickCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = () => {
    setClickCount(prev => prev + 1);
  };

  const handleAsyncClick = async () => {
    setIsLoading(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsLoading(false);
    setClickCount(prev => prev + 1);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: `
        linear-gradient(135deg, #667eea 0%, #764ba2 100%),
        radial-gradient(circle at 20% 80%, #f093fb 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, #f5576c 0%, transparent 50%),
        radial-gradient(circle at 40% 40%, #4facfe 0%, transparent 50%)
      `,
      backgroundBlendMode: 'overlay, screen, screen, screen',
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '40px',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        style={{
          textAlign: 'center',
          color: 'white',
          marginBottom: '20px'
        }}
      >
        <h1 style={{
          fontSize: '3rem',
          fontWeight: 'bold',
          margin: '0 0 10px 0',
          textShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
          background: 'linear-gradient(45deg, #fff, #f0f0f0)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Liquid Glass Button
        </h1>
        <p style={{
          fontSize: '1.2rem',
          opacity: 0.9,
          margin: '0',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          Advanced glass morphism with Framer Motion animations
        </p>
      </motion.div>

      {/* Variants Section */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          width: '100%',
          maxWidth: '800px'
        }}
      >
        <h2 style={{
          color: 'white',
          fontSize: '2rem',
          margin: '0',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          Button Variants
        </h2>
        <div style={{
          display: 'flex',
          gap: '20px',
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}>
          <LiquidGlassButton variant="primary" onClick={handleClick}>
            Primary
          </LiquidGlassButton>
          <LiquidGlassButton variant="secondary" onClick={handleClick}>
            Secondary
          </LiquidGlassButton>
          <LiquidGlassButton variant="success" onClick={handleClick}>
            Success
          </LiquidGlassButton>
          <LiquidGlassButton variant="danger" onClick={handleClick}>
            Danger
          </LiquidGlassButton>
        </div>
      </motion.div>

      {/* Sizes Section */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          width: '100%',
          maxWidth: '800px'
        }}
      >
        <h2 style={{
          color: 'white',
          fontSize: '2rem',
          margin: '0',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          Button Sizes
        </h2>
        <div style={{
          display: 'flex',
          gap: '20px',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <LiquidGlassButton size="small" onClick={handleClick}>
            Small
          </LiquidGlassButton>
          <LiquidGlassButton size="medium" onClick={handleClick}>
            Medium
          </LiquidGlassButton>
          <LiquidGlassButton size="large" onClick={handleClick}>
            Large
          </LiquidGlassButton>
        </div>
      </motion.div>

      {/* Interactive Section */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          width: '100%',
          maxWidth: '800px'
        }}
      >
        <h2 style={{
          color: 'white',
          fontSize: '2rem',
          margin: '0',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          Interactive Examples
        </h2>
        <div style={{
          display: 'flex',
          gap: '20px',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <LiquidGlassButton 
            variant="primary" 
            onClick={handleClick}
            style={{ minWidth: '150px' }}
          >
            Clicked: {clickCount}
          </LiquidGlassButton>
          <LiquidGlassButton 
            variant="success" 
            onClick={handleAsyncClick}
            disabled={isLoading}
            style={{ minWidth: '150px' }}
          >
            {isLoading ? 'Loading...' : 'Async Action'}
          </LiquidGlassButton>
          <LiquidGlassButton 
            variant="secondary" 
            disabled={true}
            style={{ minWidth: '150px' }}
          >
            Disabled
          </LiquidGlassButton>
        </div>
      </motion.div>

      {/* Features List */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.8 }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          width: '100%',
          maxWidth: '800px'
        }}
      >
        <h2 style={{
          color: 'white',
          fontSize: '2rem',
          margin: '0',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          Technical Features
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '20px',
          width: '100%'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h3 style={{ color: 'white', margin: '0 0 10px 0' }}>Glass Effects</h3>
            <ul style={{ color: 'white', margin: 0, paddingLeft: '20px' }}>
              <li>backdrop-filter: blur(20px)</li>
              <li>rgba/hsla backgrounds</li>
              <li>background-blend-mode</li>
              <li>mix-blend-mode effects</li>
              <li>Glass borders & shadows</li>
            </ul>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h3 style={{ color: 'white', margin: '0 0 10px 0' }}>Animations</h3>
            <ul style={{ color: 'white', margin: 0, paddingLeft: '20px' }}>
              <li>Framer Motion integration</li>
              <li>Parallax mouse tracking</li>
              <li>Spring animations</li>
              <li>Hover & tap effects</li>
              <li>Ripple effects</li>
            </ul>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h3 style={{ color: 'white', margin: '0 0 10px 0' }}>Customization</h3>
            <ul style={{ color: 'white', margin: 0, paddingLeft: '20px' }}>
              <li>Multiple variants</li>
              <li>Size options</li>
              <li>Disabled states</li>
              <li>Custom styling</li>
              <li>Accessible design</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ButtonDemo; 